import { useEffect, useRef, useState } from 'react';
import './App.css';
import './devlink/global.css';
import Millis from '@millisai/web-sdk';
import type Voice from '@millisai/web-sdk';
import AudioVisualizer from './AudioVisualizer';
import { BorderButton, ButtonWithIcon, ClearButton, Navbar } from './devlink';
import Iconify from './components/Iconify';
import {Box, Chip, Stack, ThemeProvider, Typography, createTheme, styled, Snackbar} from '@mui/material';

enum State {
  IDLE,
  CONNECTING,
  READY,
}

const LANGUAGE_CODES = {
  "en": "English",
  "en-US": "English (US)",
  "en-AU": "English (Australia)",
  "en-GB": "English (UK)",
  "en-NZ": "English (New Zealand)",
  "en-IN": "English (India)",
  "multi": "English/Spanish mix",
  "bg": "Bulgarian",
  "zh": "Chinese (Mandarin, Simplified)",
  "zh-TW": "Chinese (Mandarin, Traditional)",
  "cs": "Czech",
  "da": "Danish",
  "nl": "Dutch",
  "fi": "Finnish",
  "fr": "French",
  "fr-CA": "French (Canada)",
  "de": "German",
  "de-CH": "German (Switzerland)",
  "el": "Greek",
  "hi": "Hindi",
  "hu": "Hungarian",
  "id": "Indonesian",
  "it": "Italian",
  "ja": "Japanese",
  "ko": "Korean",
  "ms": "Malay",
  "no": "Norwegian",
  "pl": "Polish",
  "pt": "Portuguese",
  "pt-BR": "Portuguese (Brazil)",
  "ro": "Romanian",
  "ru": "Russian",
  "sk": "Slovak",
  "es": "Spanish",
  "es-419": "Spanish (Latin America)",
  "sv": "Swedish",
  "tr": "Turkish",
  "uk": "Ukrainian",
  "vi": "Vietnamese"
}
const CARTESIA_LANGUAGES: Record<string, string> = {
  "en": "829ccd10-f8b3-43cd-b8a0-4aeaa81f3b30",
  "en-US": "829ccd10-f8b3-43cd-b8a0-4aeaa81f3b30",
  "en-AU": "043cfc81-d69f-4bee-ae1e-7862cb358650",
  "en-GB": "a01c369f-6d2d-4185-bc20-b32c225eab70",
  "en-NZ": "043cfc81-d69f-4bee-ae1e-7862cb358650",
  "en-IN": "ff1bb1a9-c582-4570-9670-5f46169d0fc8",
  "fr": "a249eaff-1e96-4d2c-b23b-12efa4f66f41",
  "de": "3f4ade23-6eb4-4279-ab05-6a144947c4d5",
  "es": "db832ebd-3cb6-42e7-9d47-912b425adbaa",
  "pt": "700d1ee3-a641-4018-ba6e-899dcadc9e2b",
  "zh": "e90c6678-f0d3-4767-9883-5d0ecf5894a8",
  "ja": "2b568345-1d48-4047-b25f-7baccf842eb0"
}

const FAQS = [
  "The median latency of Millis AI is 600 milliseconds. The lowest latency it can get is 100 milliseconds. Our goal is to reach 300 milliseconds median latency which is the golden standard of conversational speech. If you experience latency issues, it could be some network issue, or you might be far away from us. The agent you're talking to is in the US.",
  "We're offering affordable pricing model, starting as low as 2 cents per minute for Millis cost, plus other providers's cost, such as voice providers that you choose for your agent. We're actively working on bringing the price down. Reach out to us for more details.",
  `We support multiple languages. You can customize your voice agent to speak any of the following languages: ${Object.values(LANGUAGE_CODES)}`,
  "For integration, Millis offers both low code and nocode solution for you to create voice agents. For low code option, Millis AI provides api and sdks for you to integrate with your system. We will support web sdk, python and kotlin for mobile.",
  "We give you $5 of credits to start using our services. Reach out to us for more details.",
  "Millis AI is HIPPA compliant. We're also in process of getting SOC 2.0 compliant as well.",
  "To get started, go to playground and create your voice agent. You can customize the voice agent with your own prompts there.",
  "If you're interested in using our APIs to build voice agents, reach out to us to get access. We're inviting customers to our private beta now."
]

const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

interface HomeProps {
  agentId: string;
  description: string;
  header: React.ReactNode;
}

function Base({ agentId, description, header }: HomeProps) {
  const [callState, setCallState] = useState(State.IDLE);
  const [timeout, setTimeout] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [action, setAction] = useState("");
  const [analyzer, setAnalyzer] = useState<AnalyserNode | null>(null);
  const [latency, setLatency] = useState(0);

  const [open, setOpen] = useState(false);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    setOpen(false);
    setAction("");
  };

  useEffect(() => {
    if (action) {
      setOpen(true);
    }
  }, [action]);

  const [client, setClient] = useState(Millis.createClient({publicKey: "millis_ai_demo_key"}));

  useEffect(() => {
    const client = Millis.createClient({publicKey: "millis_ai_demo_key"});
    setClient(client);
    client.on("onopen", () => {
    });

    client.on("onready", () => {
      setCallState(State.READY);
    });

    client.on("ai_action", (action) => {
      setAction(action);
    });

    client.on("onsessionended", () => {
      setTimeout(true);
    });

    client.on("onaudio", (audio: Uint8Array) => {
    });

    client.on("onresponsetext", (text: string) => {
      setTranscript(text);
    });

    client.on("onlatency", (latency: number) => {
      setLatency(latency);
    });

    client.on("analyzer", (analyzer: AnalyserNode) => {
      setAnalyzer(analyzer);
    });

    client.on("onclose", ({ code, reason }) => {
      setCallState(State.IDLE); // Update button to "Start" when conversation ends
      setAnalyzer(null);
      setTranscript("");
    });

    client.on("onerror", (error) => {
      console.error("An error occurred:", error);
      setCallState(State.IDLE); // Update button to "Start" in case of error
      setAnalyzer(null);
      setTranscript("");
    });

    client.on("ontranscript", (data) => {
      setTranscript(data);
    });
  }, []);

  const toggleConversation = async () => {
    if (callState === State.READY) {
      client.stop();
    } else if (callState === State.IDLE) {
      client.start({
        agent: {
          agent_id: agentId,
          agent_config: {
            timezone: localTimezone,
            flow: {
              agent_terminate_call: {
                enabled: true,
              }
            },
            session_timeout: {
              max_duration: 3 * 60,
              max_idle: 1 * 60
            }
          }
        }
      }).catch(console.error);
      setCallState(State.CONNECTING);
    }
  };

  const btnText = () => {
    switch (callState) {
      case State.READY: return "Stop Demo";
      case State.CONNECTING: return "Connecting...";
      default: return "Start Demo";
    }
  }

  const btnIcon = () => {
    switch (callState) {
      case State.READY: return "f7:mic-slash";
      case State.CONNECTING: return "tabler:dots";
      default: return "f7:mic";
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
    <div className="App">
      <div className="App-bkg" />
      <Navbar navMenuLeftSlot={<></>} navButtonsRightSlot={
        <>
          <BorderButton text="Build Your Agent" runtimeProps={{
          options: {
            href: "https://app.millis.ai/",
            target: '_blank'
          }
        }} />
        </>
      } />
      <div style={{position: "absolute", top: "25%", textAlign: "center", width: "100%", transform: "translate(0%, -50%)" }}>
        <h3>{header}</h3>
        <Chip label={description} variant="outlined" />
      </div>
      <div style={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)"}}>
        {callState === State.READY && <AudioVisualizer analyser={analyzer} />}
      </div>
      <header style={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)"}}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={4}
        >
          <Box sx={{width: 200, pr: 6}}></Box>
          <button disabled={timeout} style={{ filter: "drop-shadow(0 0 25px rgba(169 252 129 / 50%))", backgroundImage: 'linear-gradient(115deg, rgb(46 46 46 / 100%), rgb(46 46 46 / 82%))', border: "1px solid rgb(255 255 255 / 24%)", fontSize: 14, width: 160, height: 160, borderRadius: "50%", cursor: "pointer", ...(timeout ? {color: "grey"}: {})}} onClick={toggleConversation}>
            <div style={{display:"flex", flexDirection:"column", alignItems: "center", gap: "8px"}}>
            <div style={{fontSize: 48}}><Iconify icon={btnIcon()} /></div>
            {btnText()}
            </div>
          </button>
          <Box sx={{width: 200}}></Box>
        </Stack>
      </header>
      <div className="text-transcript" style={{position: "absolute", top: "75%", left: "50%", transform: "translate(-50%, -50%)"}}>
        <TextContainer text={!timeout ? transcript : "Please contact us if you would like a longer demo."} />
      </div>
      <div style={{position: "absolute", bottom: "8px", left: "50%", transform: "translate(-50%, 0%)"}}>
        <p className="text-grey">Like what you hear?</p>
        <div style={{display:"flex", flexDirection:"row", justifyContent: "center", gap: "8px"}}>
          <ButtonWithIcon text="Get In Touch" iconSlot={<Iconify icon="mdi:hand-wave-outline" />} buttonRuntimeProps={{
            options: {
              href: "https://millis.ai/contact",
              target: '_blank'
            }
          }} />
          <ClearButton text="Learn More" runtimeProps={{
            options: {
              href: "https://millis.ai",
              target: '_blank'
            }
          }} />
        </div>
        <div style={{display:"flex", flexDirection:"row", justifyContent: "center", gap: "8px", marginTop: "16px", fontSize: 14}}>
          <span className="text-grey">Latency: </span>
          <span>{latency}ms</span>
        </div>
      </div>
      {action && <Snackbar open={open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ position: "absolute", top: "70%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none"}}
      >
        <Chip icon={<Iconify icon="charm:robot" width={20} height={20} />} label={action} color="success"  />
      </Snackbar>}
    </div>
    </ThemeProvider>
  );
}

const Container = styled('div')({
  width: '100%',
  height: '4.8em', // Adjust this value based on your font size and line height to fit exactly two lines
  overflowY: 'hidden',
  position: 'relative',
});

const ScrollableText = styled(Typography)({
  width: '100%',
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  overflow: 'hidden',
});


const TextContainer = ({ text }: {text: string}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [text]);

  return (
    <Container ref={containerRef}>
      <ScrollableText>
        {text}
      </ScrollableText>
    </Container>
  );
};

export default Base;
